/**
 * TreeTable — a shared primitive (`KLQ-20260824-001`), modelled on ayon-frontend's
 * `shared/src/containers/ProjectTreeTable`.
 *
 * FIVE RULES, EACH THE REASON THIS IS A COMPONENT AND NOT A SCREEN
 *
 * 1. **One table, two modes — never two tables.** `mode` chooses whether the same rows are
 *    drawn nested under their parents or as one flat list; nothing else changes. Two
 *    components for the two arrangements is how a column gets added to one of them and
 *    forgotten in the other, which is precisely the drift a "list view" and a "hierarchy
 *    view" fall into when they are written separately.
 * 2. **It knows nothing about what it is rendering.** No task, no drawing, no status. A row
 *    is whatever the caller's `Row` is, and every cell is a function the caller supplies —
 *    the first consumer is the tasks hierarchy view, the expected ones are the drawings tree
 *    and the planliste register, and a primitive that named any of the three would have to be
 *    forked by the other two.
 * 3. **Group rows are structure, not data.** A node marked `group: true` exists to hold
 *    children (a location, a section, a bucket); flat mode drops those rows and keeps the
 *    rest, because flattening a tree of headers produces a list of headers nobody asked for.
 * 4. **Expansion is tracked as what is *closed*.** A branch that arrives after the first
 *    render is open rather than hidden behind a set nobody added it to — the same call
 *    `features/drawings/DrawingsPage.tsx` already made, for the same reason.
 * 5. **Selection is opt-in and lives outside** (`KLQ-20260825-006` §B.6). A surface that
 *    mounts a `<SelectionProvider>` above this table gets the select column, the cell marks
 *    and the keyboard; one that does not gets exactly today's table, with no selection code
 *    paths at all. The primitive does not fork into a selectable and a plain version, and it
 *    still owns no opinion about what a selection *means* — that is the consumer's.
 *
 * **Two layouts, one table** (`KLQ-20260905-002` §C). Under 600px of its *own* box the same
 * rows are drawn as one card each instead of as a grid — a phone, or a tablet split view,
 * which is why the width is measured on the wrapper and never on the viewport. It is not a
 * third mode and not a second component: the nodes, the columns and the collapsed set are the
 * ones above, and each column says in one word (`mobile`) what it becomes there. Nothing else
 * comes across — no select column, no cell grid, no editor, no menu (brief 25: no multi-select
 * and no bulk actions on a phone) — so the narrow half is markup and a stylesheet, not a
 * second set of rules to keep in step with these.
 *
 * Copy is split by ownership: the domain's words (the header of each column, the accessible
 * name of the table, the empty sentence) come from the consumer as props, and the chrome's
 * own words — expand, collapse, select — come from the `app` namespace, which is where the
 * application shell's copy already lives.
 *
 * It is a real `<table>` rather than a grid of divs: columns then align without a
 * `display: contents` trick, and `<th scope="col">` is a header a screen reader already
 * knows how to announce. `role="treegrid"` is deliberately **not** claimed — that role
 * promises arrow-key tree navigation, and promising it without implementing it is worse than
 * a plain table with `aria-level` and `aria-expanded` on the rows, which is what this is.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent, MouseEvent, ReactNode } from "react";

import { CheckCheck, ClipboardPaste, Copy, Minus } from "lucide-react";

import {
  parseClipboard,
  readClipboard,
  serializeSelection,
  writeClipboard,
} from "@/lib/grid-interop";
import type { ImportAdapter } from "@/lib/grid-interop";
import { useOptionalContextMenu } from "@/components/ContextMenu";
import type { MenuItem } from "@/components/MenuPopup";
import { useMessages } from "@/lib/i18n";
import { Twisty, TwistySpacer } from "@/components/Twisty";

import { CellEditor } from "./CellEditor";
import { ImportDrawer } from "./ImportDrawer";
import { cellKey, SELECT_COLUMN_KEY, sameCell } from "./cell";
import type { CellKey, CellRef } from "./cell";
import { CONFIRM_ABOVE, useOptionalEditing } from "./editing";
import type { EditBatch } from "./editing";
import { useAnchoredWindow } from "@/lib/windowing";

import { collapsedByDefault, lines } from "./lines";
import type { Line } from "./lines";
import { useOptionalSelection } from "./selection";
import type { SelectionApi } from "./selection";
import styles from "./TreeTable.module.css";
import cards from "./TreeTableCards.module.css";

/** Nested under their parents, or all of them in one flat list. See rule 1. */
export type TreeTableMode = "flat" | "grouped";
export const TREE_TABLE_MODES: readonly TreeTableMode[] = ["grouped", "flat"];

export interface TreeTableNode<Row> {
  /** Unique across the whole tree — it is the React key and the expansion identity. */
  readonly id: string;
  readonly row: Row;
  readonly children?: readonly TreeTableNode<Row>[];
  /** Structure rather than data: drawn in `grouped` mode, dropped in `flat`. Rule 3. */
  readonly group?: boolean;
  /**
   * **This row is a notice about the list, not a row of it** — the bottom-edge sentinel that
   * says *more is coming*, and anything else that is a sentence rather than a record.
   *
   * It changes nothing in the grid, where a table row already reads as a row of a table. It
   * changes the cards, and it has to: a card is a bordered box with a title and a meta line, so
   * a sentinel drawn as one is a task called *"Loading more…"* with no assignee and no date.
   * Added 2026-09-21, when the phone pass found exactly that in `TaskHierarchy` (`my_notes`:
   * *"sentinel riadok vo vetve — či je čitateľný keď je karta a nie riadok tabuľky"* — it was
   * not). A notice keeps its indentation, so it is still visibly inside its branch, and loses
   * the box, the twisty column and the meta line.
   *
   * **Not `group`.** A group is structure the reader navigates and folds; a notice is neither,
   * and collapsing the two would put a twisty on a sentence.
   */
  readonly notice?: boolean;
  /**
   * Whether this row has a twisty, when `children` is not the answer.
   *
   * Default: it has one when it has children. A consumer that **fetches a branch's contents
   * when the branch is opened** (`KLQ-20260825-008` §5.1) has to say so explicitly, because
   * otherwise the row it has not fetched yet is drawn as a leaf and the only gesture that
   * would fetch it is the one it declines to offer.
   */
  readonly expandable?: boolean;
}

export interface TreeTableColumn<Row> {
  readonly key: string;
  readonly header: string;
  /** A `<col>` width. The first column carries the twisty and the indentation. */
  readonly width: string;
  readonly cell: (row: Row) => ReactNode;
  /**
   * What this cell *is*, as text — `KLQ-20260828-001` §A.2/§A.3. One definition serving the
   * clipboard, the editor's starting point, and the "did anything actually change?" check.
   * Rendered and human-readable (a name, a status label, an ISO date), never an internal id.
   * Optional today; a column without it is simply not copied.
   */
  readonly value?: (row: Row) => string;
  /**
   * **Where this row lives, as a path this app can route to** — `null` for a row that is not
   * anywhere (owner note, 2026-09-10).
   *
   * It is the clipboard's only consumer today: a register copied into a sheet used to be a
   * grid of names with no way back, so *"4.NP"* in somebody's spreadsheet was a dead end. With
   * this, the HTML flavour wraps the same text in a link and the cell stays clickable in the
   * sheet.
   *
   * **A path, not a URL.** The column knows the route; only the table knows which origin the
   * reader is on, and it is the table that makes the link absolute — a relative href in a
   * document owned by Google resolves against Google.
   *
   * Beside `value` rather than derived from `cell`, because what a cell *draws* and where the
   * row *is* are different questions: the drawings register's first column is a button opening
   * a panel, and the row behind it still has an address.
   */
  readonly href?: (row: Row) => string | null;
  /**
   * What this column becomes when the table is drawn as cards (`KLQ-20260905-002` §C).
   *
   * `title` is the card's first line and exactly one column may claim it — the first column
   * is the default title. `meta` joins the second line. `hidden` is absent from the card and
   * reachable in the row's detail. A column that says nothing is `meta`, so nothing
   * disappears from a phone by omission.
   */
  readonly mobile?: "title" | "meta" | "hidden";
  /**
   * Present iff the column can be written — `KLQ-20260828-001` §A.2. Absent means read-only
   * everywhere, for typing and for paste alike: one declaration, both doors.
   */
  readonly edit?: TreeTableEdit<Row>;
}

export interface TreeTableEditOption {
  readonly id: string;
  readonly label: string;
}

/** How a value is picked. `select` and `multiselect` carry `options`; the other two are typed. */
export type TreeTableEditKind = "text" | "date" | "select" | "multiselect";

/**
 * The kind for one row — `editKindOf`. A scalar for a column that means the same thing on
 * every row it applies to, a function for one that does not.
 *
 * **A function is not over-engineering here, it is the same shape `applies` already has.** A
 * heterogeneous tree has columns whose *question* is constant and whose *answer shape* is not:
 * `Kto` asks "who is on this row" of a task and of a level alike, and gets back a list of
 * assignees from one and a single responsible person from the other (`KLQ-20260828-003`).
 * Splitting that into two columns puts the same question in two places on one screen, which is
 * what the owner refused on 2026-08-28 — so the kind moved instead.
 */
export type TreeTableEditKindOf<Row> =
  | TreeTableEditKind
  | ((row: Row) => TreeTableEditKind);

export function editKindOf<Row>(
  kind: TreeTableEditKindOf<Row>,
  row: Row,
): TreeTableEditKind {
  return typeof kind === "function" ? kind(row) : kind;
}

export interface TreeTableEdit<Row> {
  /** How the value is picked — one kind, or one per row. See `TreeTableEditKindOf`. */
  readonly kind: TreeTableEditKindOf<Row>;
  readonly options?: (row: Row) => readonly TreeTableEditOption[];
  /**
   * Whether *this row* can take this field at all. A container has no assignee, and a
   * heterogeneous tree is exactly why this is a function of the row rather than of the
   * column — a mixed selection is filtered by it, never refused because of it (§C.1).
   */
  readonly applies: (row: Row) => boolean;
  /**
   * The row's value now, in whatever shape the writer takes. Two readers: the undo stack,
   * which restores each row's *own* previous value, and the editor's starting point when the
   * column also declares `value()`.
   */
  readonly current: (row: Row) => unknown;
  /**
   * This row, with this column's value replaced — `KLQ-20260828-001` §F.1.
   *
   * The primitive cannot patch a row it knows nothing about, and rendering raw text in place
   * of the column's own cell would swap a status pill for a word mid-write. So the column —
   * the one thing that knows both shapes — puts the value back. A column that omits this is
   * simply not shown optimistically.
   */
  readonly optimistic?: (row: Row, value: unknown) => Row;
  /**
   * How many *further* rows will display this value without being written —
   * `KLQ-20260828-003` §F.2. Given the rows the edit actually reaches; answers `0` when
   * nothing inherits.
   *
   * The primitive cannot compute this and must not try: *"which rows show a value they did not
   * state"* is a rule about one column's meaning (a level's responsible reaches the work
   * beneath it; a due date reaches nobody), and only the surface that declared the column
   * knows it. What the primitive owns is that the number is **stated separately** from the
   * write count rather than folded into it — a fan-out count is honest about a write and
   * misleading about an effect, and one number cannot be both.
   */
  readonly reach?: (rows: readonly Row[]) => number;
  /** Text — typed, or pasted from a sheet — to a value, or a reason this row cannot take it. */
  readonly parse: (
    input: string,
    row: Row,
  ) =>
    | { readonly ok: true; readonly value: unknown }
    | { readonly ok: false; readonly reason: string };
}

export interface TreeTableProps<Row> {
  /** The table's accessible name. The consumer's word for what is in it. */
  readonly label: string;
  readonly columns: readonly TreeTableColumn<Row>[];
  readonly nodes: readonly TreeTableNode<Row>[];
  readonly mode: TreeTableMode;
  /**
   * How many levels are open before anybody presses anything. `1` — the default — draws the
   * root and its children and closes the rest.
   *
   * A surface raises it when its tree is the point rather than the container: a picker with two
   * levels reads as broken when the second one is shut. It is a *rendering* bound and says
   * nothing about what has been fetched (`KLQ-20260825-008` §5.1: shape whole, contents per
   * branch, rendering bounded on its own).
   */
  readonly openToDepth?: number;
  /**
   * The branches currently open, whenever that set changes — **the fetch trigger**.
   *
   * A screen that holds the tree's shape and fetches its contents per branch
   * (`KLQ-20260825-008` §5.1) needs to know which rows are showing their children, and the
   * table is the only thing that knows: which branches are open is its state, and the default
   * is derived from the nodes rather than chosen by anybody. So it says so, once per change,
   * with the ids of every drawn expandable row that is open.
   *
   * Ids, ordered as drawn, deduplicated by construction. Called on mount with the default, so
   * a consumer never has to reproduce `openToDepth`'s arithmetic to know where it started.
   *
   * **Must be stable** — a `useState` setter, or wrapped in `useCallback`. It is in the
   * effect's dependencies, and a function rebuilt every render would fire this on every render
   * of the page around it.
   */
  readonly onOpenChange?: (open: readonly string[]) => void;
  /** One row's name, for the twisty's accessible label. Never rendered as text. */
  readonly rowLabel: (row: Row) => string;
  /** Printed instead of the body when there is nothing to draw. */
  readonly empty: ReactNode;
  /**
   * Whether a drawn row may be **ticked** — whether it is a subject of a bulk action.
   * Structure rows (a floor, a bucket) are pointed at with the twisty, not ticked; ayon
   * returns `null` for its group rows in the selection column for the same reason
   * (`buildTreeTableColumns.tsx:354`). Default: every non-group row.
   *
   * **This answered a second question too, and it should not have** (`KLQ-20260828-003`,
   * 2026-08-28). *"May this row be the subject of a bulk action?"* and *"may the keyboard reach
   * this row's cells and open one?"* are different questions, and one predicate answering both
   * meant a floor could not be edited because a floor must not be deletable in bulk. The second
   * question is `focusable` below, and the split is what lets a level carry an editable column
   * without `KLQ-20260825-006` §D.1 losing its meaning — a selected container still stands for
   * its loaded descendants, because ticking it is still what a selection *is*.
   */
  readonly selectable?: (row: Row) => boolean;
  /**
   * Whether a drawn row takes part in the **cell grid**: keyboard focus, cell ranges, and the
   * editor. Defaults to `selectable`, so a surface that never asked the question keeps exactly
   * the behaviour it had.
   *
   * A row that is focusable but not selectable has no tick and cannot be bulk-acted on, and its
   * cells can still be arrowed to and opened. That is a container level: *point at it with the
   * twisty, edit what belongs to it.*
   */
  readonly focusable?: (row: Row) => boolean;
  /**
   * What a right-click — or Shift+F10 — on these rows should offer (`KLQ-20260825-006` §C).
   *
   * The **rows are handed in**, not read out of the selection by the caller, and that is the
   * whole reason this is a callback and not a `menuItems` array. A right-click outside the
   * selection moves the selection first, and React has not applied that move by the time the
   * items have to exist; a constructor reading `selection.rows` would build its list for the
   * rows somebody pointed at a moment ago. So the gesture resolves *which rows this click
   * means* here — where the answer is known — and the surface only has to say what may be
   * done to them.
   */
  readonly menuItems?: (rowIds: readonly string[]) => readonly MenuItem[];
  /** The menu's accessible name, in the consumer's words. Required by `menuItems`. */
  readonly menuLabel?: string;
  /**
   * **The table's own two actions, handed to the surface as items** — copy and paste.
   *
   * They belong on both menus a selection has (`KLQ-20260825-006` §C: one item model, drawn by
   * the ⋯, the right-click and the action bar), and only one of those is opened by this
   * component — the action bar is the surface's. So rather than injecting them into the menu it
   * happens to own and leaving the bar without them, the table *publishes* them and the surface
   * decides where they sit.
   *
   * **Items and not callbacks**, so the words come from `app` with the rest of the table's
   * chrome instead of every surface inventing a translation for *Copy*. Registered the way
   * `registerGrid` is: on mount and whenever the selection changes what they mean.
   */
  readonly registerClipboard?: (items: readonly MenuItem[]) => void;
  /**
   * What a pasted sheet means *here* — `KLQ-20260824-002`, the import branch.
   *
   * `Cmd+V` has two destinations and the selection alone chooses between them: cells selected
   * → overwrite them through `KLQ-20260828-001`; nothing selected → hand the parsed grid to
   * this adapter and open the preview. A surface that declares none still *copies*; pasting
   * into it says "not supported here yet" rather than doing nothing, because a keystroke that
   * produces no effect at all is indistinguishable from a broken table.
   */
  // `| undefined` explicitly, because `exactOptionalPropertyTypes` is on and every consumer
  // decides *at render* whether it has an adapter yet — a surface whose tree has not arrived
  // has none, and that is a value it must be able to pass.
  readonly importAdapter?: ImportAdapter | undefined;
  /**
   * What the remembered column mapping is filed under. The surface's own name for itself —
   * required by `importAdapter`, because two tables with the same field list are still two
   * different sheets in the studio's folder.
   */
  readonly importSurface?: string;
  /** Ran after an import wrote something, so the surface can refetch what it drew. */
  readonly onImported?: () => void;
}

/**
 * Whether a key landed in something that takes text rather than in the grid.
 *
 * The cell editor stops its own keys from reaching the table, so this is not for it: it is
 * the backstop for a control a *consumer* put inside a cell, which cannot be expected to
 * know that the table above it claims `Delete`. A grid that emptied twelve rows because
 * somebody was deleting a character in a box inside one of them would be unforgivable, and
 * the check costs one `tagName`.
 */
function inField(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.tagName === "INPUT" ||
    target.tagName === "SELECT" ||
    target.tagName === "TEXTAREA" ||
    target.isContentEditable
  );
}

/** The DOM id of one cell — what `aria-activedescendant` points at. */
function domId(ref: CellRef): string {
  return `tt-${ref.rowId}-${ref.colKey}`;
}

/**
 * Which sides of a selected cell are the *edge of the block* — `KLQ-20260825-006` §B.10.
 *
 * The outline is drawn on the perimeter of the rectangle and nowhere inside it, because a
 * continuous border around a region reads as one region while a border around every cell
 * reads as a grid of little boxes. Ayon computes exactly this and calls it
 * `getCellBorderClasses` (`SelectionCellsContext.ts:41`).
 */
function edges(
  selection: SelectionApi,
  rowIds: readonly string[],
  colKeys: readonly string[],
  r: number,
  c: number,
): string {
  const rowId = rowIds[r];
  const colKey = colKeys[c];
  if (rowId === undefined || colKey === undefined) return "";
  const on = (dr: number, dc: number): boolean => {
    const otherRow = rowIds[r + dr];
    const otherCol = colKeys[c + dc];
    if (otherRow === undefined || otherCol === undefined) return false;
    return selection.isSelected({ rowId: otherRow, colKey: otherCol });
  };
  return [
    on(-1, 0) ? "" : styles.edgeTop,
    on(1, 0) ? "" : styles.edgeBottom,
    on(0, -1) ? "" : styles.edgeStart,
    on(0, 1) ? "" : styles.edgeEnd,
  ]
    .filter(Boolean)
    .join(" ");
}

/**
 * What the editor opens with.
 *
 * A picker and a text box want different things, and conflating them is a real bug rather
 * than a nicety: `value()` is the **rendered** text — a translated status, a person's name —
 * while a picker's options are keyed by id. Seeding a `<select>` from the label matches no
 * option, so the control opens blank and the person is told their task has no status. So a
 * select seeds from `edit.current()` (the id, or the first of a list of them) and everything
 * else seeds from the text the cell already shows.
 */
function seedFor<Row>(
  edit: TreeTableEdit<Row>,
  column: TreeTableColumn<Row>,
  row: Row,
): string {
  const kind = editKindOf(edit.kind, row);
  if (kind !== "select" && kind !== "multiselect") {
    return column.value?.(row) ?? "";
  }
  const current = edit.current(row);
  if (Array.isArray(current)) {
    // A multiple picker round-trips the whole list; a single one opens on the first of it,
    // which is the only honest thing a one-slot control can say about a two-name cell.
    return kind === "multiselect"
      ? current.map(String).join(",")
      : current.length === 0
        ? ""
        : String(current[0]);
  }
  // A primitive is what a text box can hold; anything else — an object a column decided to
  // keep its value as — has no honest one-line rendering, and `[object Object]` in an editor
  // is worse than an empty one.
  return typeof current === "string" ||
    typeof current === "number" ||
    typeof current === "boolean"
    ? String(current)
    : "";
}

/**
 * §C's threshold. The box's own width, never the viewport's — a tablet split view is as
 * narrow as a phone, and a table beside an open rail is narrower than the window it sits in.
 */
const CARDS_MAX_WIDTH = 600;

/**
 * One row's height in pixels — **a contract, not a look** (owner's decision, 2026-09-10).
 *
 * The table draws the rows the scroll box can show and stands two empty boxes in for the rest,
 * and the position of row *n* is `n × this`. A row that turns out taller — a title that wraps
 * onto a second line — makes every row below it drift from where the scrollbar says it is.
 *
 * So the cells do not wrap: one line, elided with `…`, the whole value in the cell's `title`
 * and in whatever the row opens. That is the trade the fixed height buys, and it is the same
 * one every register — Excel, Sheets, Finder — has already made.
 *
 * TWIN: `--tree-row-height` in `TreeTable.module.css`, which is what actually makes a row this
 * tall (retro rule 12). Change one and change the other.
 */
const ROW_HEIGHT = 36;

/**
 * The cell's whole value, as a `title` — what the elision gives back.
 *
 * `column.value` is already *"what this cell is, as text"* (`KLQ-20260828-001` §A.2): the same
 * definition the clipboard serialises and the editor starts from, so the tooltip cannot say
 * something different from what a copy would take. A column that declares no `value` gets no
 * tooltip rather than a guessed one — and an empty string gets none either, because a tooltip
 * that says nothing is a tooltip that flickers for no reason.
 */
function titleOf<Row>(
  column: TreeTableColumn<Row>,
  row: Row,
): { readonly title?: string } {
  const text = column.value?.(row) ?? "";
  return text === "" ? {} : { title: text };
}

export function TreeTable<Row>({
  label,
  columns,
  nodes,
  mode,
  rowLabel,
  empty,
  openToDepth = 1,
  onOpenChange,
  selectable,
  focusable,
  menuItems,
  menuLabel,
  registerClipboard,
  importAdapter,
  importSurface,
  onImported,
}: TreeTableProps<Row>) {
  const t = useMessages("app");
  /**
   * Which branches are shut — **`null` until somebody presses a twisty**, and that is the whole
   * of how the default works.
   *
   * A seeded `useState` cannot do this job: `nodes` is empty on the first render while the tree
   * is still loading, so a default computed then would close nothing. An effect that re-seeded
   * on every `nodes` change would throw away what the reader had opened each time the query
   * refetched. Holding `null` for "nobody has said" keeps the default a *derivation* — it
   * follows the data in — and the first press replaces it with a real set for good.
   */
  const [collapsed, setCollapsed] = useState<ReadonlySet<string> | null>(null);
  const byDefault = useMemo(
    () => collapsedByDefault(nodes, openToDepth),
    [nodes, openToDepth],
  );
  const shut = collapsed ?? byDefault;
  // The character that opened the editor, when typing was what opened it. A spreadsheet
  // starts a cell from the key you pressed rather than making you press it again, and losing
  // that first keystroke is the difference between "it types" and "it almost types".
  const [typed, setTyped] = useState<string | null>(null);
  // A `Delete` over more rows than §D.2 lets pass unasked, held until it is answered. The
  // whole batch is kept rather than rebuilt on Apply, so what is written is what was counted
  // — the selection may well have moved by the time somebody reaches for the button.
  const [clearing, setClearing] = useState<{
    readonly batches: readonly EditBatch[];
    readonly previous: ReadonlyMap<CellKey, unknown>;
    readonly rows: number;
  } | null>(null);
  // The clipboard, parsed, while the import preview is open over this table. Held here rather
  // than read again inside the drawer: the drawer must show the block the person pasted, and a
  // second `readClipboard()` a few seconds later could legitimately answer something else.
  const [importing, setImporting] = useState<readonly (readonly string[])[] | null>(
    null,
  );
  // Said in the caption, exactly where the `Delete` question is said, and cleared by the next
  // keystroke. A paste that lands nowhere has to leave *some* trace, or the table reads as
  // broken (`KLQ-20260824-002`'s last acceptance criterion).
  const [notice, setNotice] = useState<string | null>(null);
  const selection = useOptionalSelection();
  const editing = useOptionalEditing();
  const contextMenu = useOptionalContextMenu();
  /** Where focus goes back to when the menu closes on Escape (§I): the grid, which is what
      holds the selection the menu was about to act on. */
  const table = useRef<HTMLTableElement>(null);
  /**
   * The box whose width chooses the layout (§C), held in **state** rather than in a ref.
   *
   * A ref would be read once, by an effect with no dependency to re-run on, and the
   * empty-table return below draws no wrapper at all — so a register that arrives empty and
   * fills a moment later would be measured against nothing and stay wide for the rest of the
   * session. A callback ref in state re-runs the effect whenever the node appears or goes.
   */
  const [frame, setFrame] = useState<HTMLDivElement | null>(null);
  /** Cards or the grid. `false` first, so a desktop never flashes a phone layout. */
  const [narrow, setNarrow] = useState(false);

  useEffect(() => {
    if (frame === null) return undefined;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry === undefined) return;
      setNarrow(entry.contentRect.width <= CARDS_MAX_WIDTH);
    });
    observer.observe(frame);
    return () => {
      observer.disconnect();
    };
  }, [frame]);

  const drawn = useMemo(() => lines(nodes, mode, shut), [nodes, mode, shut]);

  /**
   * **Which rows are actually in the document** (`KLQ-20260820-005`). A project with every
   * branch open is thousands of `<tr>` and tens of thousands of `<td>`; this draws the ones the
   * scroll box can show plus a few, and two empty boxes stand in for the height of the others.
   *
   * `keep` is the row that must be drawn whatever the scroll says, and there are two candidates
   * for it: the cell the keyboard is on, because `aria-activedescendant` names an id that has
   * to exist, and the cell being edited, because unmounting an open editor discards what
   * somebody is typing. They are the same row almost always; when they are not, the editor wins
   * — losing typed text is worse than a stale attribute, and the two disagree only in the
   * moment between an edit being opened and focus following it.
   *
   * `content-visibility` is not an alternative here and it is worth saying why, because it
   * looks like one: CSS containment does not apply to internal table elements, so setting it on
   * a `<tr>` does nothing at all in any engine.
   */
  const kept = editing?.editing?.rowId ?? selection?.focus?.rowId ?? null;
  const keptIndex = kept === null ? -1 : drawn.findIndex((one) => one.id === kept);
  const view = useAnchoredWindow(drawn.length, ROW_HEIGHT, keptIndex);
  /** Every column the body draws, the tick column included — what a spacer row has to span. */
  const spannedColumns = columns.length + (selection === null ? 0 : 1);

  /**
   * Tell the consumer which branches are open, when that changes and not on every render.
   *
   * The identity of a fresh array changes every time, and a consumer that turns this into a
   * query key would refetch on each keystroke elsewhere on the page — so the effect depends on
   * the joined *string*, and the delimiter is a newline because an id cannot contain one.
   *
   * `drawn` rather than `shut`: shut is a set of closed ids and says nothing about which rows
   * exist, so a branch that has never been closed is not in it. What a fetching consumer needs
   * is the positive statement — these rows are showing their children — and only the walk has
   * it.
   */
  const openKey = drawn
    .filter((line) => line.expandable && line.open)
    .map((line) => line.id)
    .join("\n");
  useEffect(() => {
    onOpenChange?.(openKey === "" ? [] : openKey.split("\n"));
  }, [openKey, onOpenChange]);

  /**
   * The row as it should look right now: what the data says, with any value written and not
   * yet read back folded in (§F.1). Every renderer below reads through it, so a cell, its copy
   * value and its editor's starting point cannot disagree about what the row holds.
   */
  const shown = (line: Line<Row>): Row => {
    if (editing === null || editing.optimistic.size === 0) return line.row;
    let row = line.row;
    for (const column of columns) {
      const patch = column.edit?.optimistic;
      if (patch === undefined) continue;
      const key = cellKey({ rowId: line.id, colKey: column.key });
      if (!editing.optimistic.has(key)) continue;
      row = patch(row, editing.optimistic.get(key));
    }
    return row;
  };

  /**
   * Ticked, and therefore a subject of a bulk action.
   *
   * **The default is every non-group row; a surface that declares `selectable` decides for
   * itself, group rows included** (2026-08-28). This used to read `!line.group && …`, so a
   * consumer could only ever *narrow* the set and a structure row was unreachable however
   * loudly it asked. That was the wrong shape: `KLQ-20260825-006` §D.1 does not say a container
   * may not be ticked, it says what ticking one **means** — its loaded descendants, resolved at
   * the action layer, *"rather than pretending the row itself is a subject"*. That rule lives in
   * `planSubjects`, which has always handled a container row, and letting the tick reach one
   * leaves it exactly as true.
   *
   * `line.row`, not `shown(line)` — the same input `tickIds` uses, so a row cannot be tickable
   * in the list and untickable in the cell that draws its tick. Selectability is structural;
   * an optimistic patch changes a value, never a kind.
   */
  const canSelect = (line: Line<Row>): boolean =>
    selectable === undefined ? !line.group : selectable(line.row);

  // The grid the selection measures ranges over: exactly what is on screen, in the order it
  // is on screen (§B.3). The select column is column 0 and a column like any other — that is
  // what keeps "three rows" and "nine cells" one mechanism instead of two.
  //
  // **Built from `focusable`, not from `selectable`** (`KLQ-20260828-003`): this list is what
  // the arrows walk and what `cellProps` hands its handlers to, so a row missing from it is a
  // row no editor can ever open. A container in it still cannot be ticked — the tick column,
  // `selectAll` and the header's tri-state all ask `tickIds` below, which stays narrow.
  //
  // **Asked of `line.row` and not of `shown(line)`**, which is what keeps this memo honest
  // about its dependencies: whether a row can be reached is *structural* — it turns on the
  // row's kind — and an optimistic patch replaces a value, never a kind. Reading the patched
  // row here would make the grid a function of every in-flight write, which is both a
  // dependency this list does not want and a rebuild nobody asked for.
  const rowIds = useMemo(
    () =>
      drawn
        .filter((line) =>
          focusable !== undefined
            ? focusable(line.row)
            : selectable === undefined
              ? !line.group
              : selectable(line.row),
        )
        .map((line) => line.id),
    [drawn, selectable, focusable],
  );
  const colKeys = useMemo(
    () => [SELECT_COLUMN_KEY, ...columns.map((column) => column.key)],
    [columns],
  );

  /**
   * The same columns, as the two lines of a card (§C).
   *
   * The default is deliberately generous: a column that says nothing is `meta`, so a surface
   * that has never thought about a phone loses no facts by omission — only the columns that
   * ask to be `hidden` are dropped. Two columns claiming the title is a mistake in the
   * consumer rather than a state to render, so it fails loudly in development, the way
   * `assertColKey` does.
   */
  const card = useMemo(() => {
    const titles = columns.filter((column) => column.mobile === "title");
    if (import.meta.env.DEV && titles.length > 1) {
      throw new Error(
        `Two columns claim the card's title line (${titles
          .map((column) => column.key)
          .join(", ")}) — exactly one may.`,
      );
    }
    const title = titles[0] ?? columns[0];
    return {
      title,
      meta: columns.filter(
        (column) => column !== title && column.mobile !== "hidden",
      ),
    };
  }, [columns]);

  /**
   * The rows a tick may reach — a subset of the grid whenever a surface declares `focusable`.
   * Registered beside the grid so that *select all* and the header's tri-state count what a
   * bulk action can act on, while the arrows and the editor walk the wider list.
   */
  const tickIds = useMemo(
    () =>
      drawn
        .filter((line) =>
          selectable === undefined ? !line.group : selectable(line.row),
        )
        .map((line) => line.id),
    [drawn, selectable],
  );

  const register = selection?.registerGrid;
  useEffect(() => {
    register?.(rowIds, colKeys, tickIds);
  }, [register, rowIds, colKeys, tickIds]);

  // New rows mean the write has been read back, so the overlay has done its job. Tying it to
  // the data rather than to a timer is what stops a stale optimistic value from outliving a
  // refetch that disagreed with it.
  const settle = editing?.settle;
  const settling =
    editing !== null && editing.optimistic.size > 0 && !editing.pending;
  useEffect(() => {
    if (settling) settle?.();
  }, [nodes, settle, settling]);

  const byColumnKey = new Map(columns.map((column) => [column.key, column]));
  const [first, ...rest] = columns;

  /**
   * The clipboard, straight into the preview.
   *
   * Hoisted above the empty-table return on purpose: **an empty table is the one a studio
   * imports into.** The migration this spec exists for starts on a project with nothing in it,
   * and a paste that only works once there is already a row would miss it entirely — so the
   * empty state takes focus and claims the chord too.
   */
  const startImport = async (): Promise<void> => {
    const text = await readClipboard();
    if (text === null || text.trim() === "") return;
    const source = parseClipboard(text);
    if (source.length > 0) setImporting(source);
  };

  /* The preview is the mechanism's, not the surface's: one drawer for every table that ever
     declares an adapter, for the reason the spec gives about copy. */
  const drawer =
    importing === null || importAdapter === undefined ? null : (
      <ImportDrawer
        grid={importing}
        adapter={importAdapter}
        surface={importSurface ?? label}
        onClose={() => {
          setImporting(null);
          table.current?.focus();
        }}
        onWritten={() => {
          onImported?.();
        }}
      />
    );

  /**
   * Copy and paste as menu items, published to the surface (`registerClipboard`).
   *
   * **Declared here, above the empty-table return, and the refs are why they can be.** Both
   * functions close over the whole render — the drawn rows, the columns, the live selection —
   * so they are new objects every time, and they are also written *below* this point, past the
   * early return an empty table takes. A hook may not sit after an early return, and an item
   * list that changed identity every render would loop against a surface that stores it. One
   * ref answers both: the *items* are stable, the *doing* is whatever the current render bound.
   *
   * `hidden` rather than `disabled` when there is nothing to act on: `MenuPopup` draws a
   * disabled item to teach that a capability exists, and *Copy* over an empty selection teaches
   * nothing anybody did not already know. Paste is never hidden — whether the clipboard holds
   * anything is a question only an `await` can answer, and an item that flickered with the
   * contents of another application would be worse than one that occasionally does nothing.
   */
  const doCopy = useRef<(() => void) | null>(null);
  const doPaste = useRef<(() => Promise<void>) | null>(null);

  const anySelection = selection !== null;
  const anyCells = (selection?.cells.size ?? 0) > 0;

  const clipboardItems = useMemo<readonly MenuItem[]>(
    () => [
      {
        id: "grid-copy",
        label: t("treeTable.copy"),
        icon: <Copy size={16} strokeWidth={1.5} aria-hidden />,
        shortcut: "⌘C",
        hidden: !anyCells,
        onSelect: () => {
          doCopy.current?.();
        },
      },
      {
        id: "grid-paste",
        label: t("treeTable.paste"),
        icon: <ClipboardPaste size={16} strokeWidth={1.5} aria-hidden />,
        shortcut: "⌘V",
        hidden: !anySelection,
        onSelect: () => {
          void doPaste.current?.();
        },
      },
    ],
    [t, anyCells, anySelection],
  );

  const publish = registerClipboard;
  useEffect(() => {
    publish?.(clipboardItems);
  }, [publish, clipboardItems]);

  if (first === undefined || drawn.length === 0) {
    return (
      <>
        <div
          className={styles.empty}
          tabIndex={importAdapter === undefined ? undefined : 0}
          onKeyDown={(event) => {
            if (importAdapter === undefined) return;
            if (!(event.metaKey || event.ctrlKey)) return;
            if (event.key.toLowerCase() !== "v") return;
            event.preventDefault();
            void startImport();
          }}
        >
          {empty}
        </div>
        {drawer}
      </>
    );
  }

  const move = (deltaRow: number, deltaCol: number, extend: boolean): void => {
    if (selection === null) return;
    const from = selection.focus ?? {
      rowId: rowIds[0] ?? "",
      colKey: colKeys[0] ?? "",
    };
    const r = rowIds.indexOf(from.rowId);
    const c = colKeys.indexOf(from.colKey);
    if (r === -1 || c === -1) return;
    const rowId =
      rowIds[Math.min(Math.max(r + deltaRow, 0), rowIds.length - 1)];
    const colKey =
      colKeys[Math.min(Math.max(c + deltaCol, 0), colKeys.length - 1)];
    if (rowId === undefined || colKey === undefined) return;
    selection.moveFocus({ rowId, colKey }, extend);
  };

  /**
   * The selection, as a spreadsheet block (`KLQ-20260824-002`).
   *
   * The table is the only place that knows the rendered order of both axes and how to turn a
   * row into text, so it is where the source is assembled — but the serializing itself lives
   * in `lib/grid-interop`, pure and testable, and is shared with every other table that will
   * ever mount a selection.
   */
  const copy = (): void => {
    if (selection === null) return;
    const rowById = new Map(drawn.map((line) => [line.id, shown(line)]));
    const byKey = new Map(columns.map((column) => [column.key, column]));
    const payload = serializeSelection(
      {
        rowIds,
        // Data columns only: the select column is a control, not a value, and pasting a
        // column of ticks into somebody's sheet would be a column of nothing.
        colKeys: columns.map((column) => column.key),
        headerOf: (colKey) => byKey.get(colKey)?.header ?? colKey,
        valueOf: (rowId, colKey) => {
          const row = rowById.get(rowId);
          const column = byKey.get(colKey);
          if (row === undefined || column?.value === undefined)
            return undefined;
          return column.value(row);
        },
        // The column supplies the path, this supplies the origin — see `href` on the column.
        // `window.location.origin` and not a configured base: the link is meant to lead back
        // to the deployment the person was reading, so a copy taken from staging goes to
        // staging and one taken from production goes to production, with nothing to keep in
        // sync and nothing to get wrong.
        linkOf: (rowId, colKey) => {
          const row = rowById.get(rowId);
          const column = byKey.get(colKey);
          if (row === undefined || column?.href === undefined) return undefined;
          const path = column.href(row);
          if (path === null) return undefined;
          try {
            return new URL(path, window.location.origin).toString();
          } catch {
            return undefined;
          }
        },
      },
      (rowId, colKey) => selection.isSelected({ rowId, colKey }),
    );
    // Nothing selected: leave the browser's own copy alone rather than blanking the clipboard.
    if (payload === null) return;
    void writeClipboard(payload);
  };

  /**
   * Which rows an edit at this cell reaches — `KLQ-20260828-001` §C.1.
   *
   * The selection intersected with the edited column, then filtered by `applies`. If the
   * edited cell is **not** part of the selection, the answer is that cell alone: a stray click
   * after a big selection must not fan a value across it, and this is the one place where
   * being generous would be dangerous.
   */
  const fanOut = (ref: CellRef, column: TreeTableColumn<Row>): Line<Row>[] => {
    const here = drawn.filter((line) => line.id === ref.rowId);
    if (!selection?.isSelected(ref)) return here;
    const reached = drawn.filter(
      (line) =>
        selection.isSelected({ rowId: line.id, colKey: ref.colKey }) &&
        (column.edit?.applies(shown(line)) ?? false),
    );
    return reached.length === 0 ? here : reached;
  };

  /**
   * Open the menu for a cell — `KLQ-20260825-006` §C, §G.
   *
   * Two rules, both of them the ones every grid in every spreadsheet already follows:
   *
   *   - **A right-click inside the selection leaves it alone**; one outside it moves the
   *     selection to the row it landed on. Anything else means a menu whose first item acts on
   *     forty rows somebody stopped looking at two clicks ago.
   *   - **The rows are resolved here**, not by the constructor. The move above is a state
   *     update React has not applied yet, so the honest answer to *"which rows does this click
   *     mean?"* exists in this function and nowhere else until the next render.
   */
  const openMenu = (ref: CellRef, at: { x: number; y: number }): void => {
    if (contextMenu === null || menuItems === undefined || selection === null)
      return;
    const inside = selection.isRowSelected(ref.rowId);
    if (!inside) selection.press(ref, { shift: false, additive: false });
    const rowIds = inside ? selection.rows : [ref.rowId];
    contextMenu.open({
      label: menuLabel ?? label,
      items: menuItems(rowIds),
      x: at.x,
      y: at.y,
      returnFocus: table.current,
    });
  };

  const startEditing = (ref: CellRef, column: TreeTableColumn<Row>): void => {
    if (editing === null || column.edit === undefined) return;
    const line = drawn.find((one) => one.id === ref.rowId);
    if (line === undefined || !column.edit.applies(shown(line))) return;
    editing.open(ref);
  };

  const commitEdit = (
    ref: CellRef,
    column: TreeTableColumn<Row>,
    input: string,
    andMove: "down" | "right" | null,
  ): void => {
    const edit = column.edit;
    if (editing === null || edit === undefined) return;
    const reached = fanOut(ref, column);
    const anchorLine = drawn.find((one) => one.id === ref.rowId);
    if (anchorLine === undefined) return;

    // The anchor is parsed first and only to decide whether the *input* is acceptable at all —
    // a mistyped date is refused here rather than in twelve requests. A client-side courtesy;
    // the authority is the server, which validates per row.
    if (!edit.parse(input, anchorLine.row).ok) {
      editing.cancel();
      return;
    }

    /**
     * **Then parsed again, per row, and grouped by what it produced** — the same shape
     * `clearCells` has always had, brought here on 2026-08-28 when the first column whose
     * *answer shape* varies by row landed (`KLQ-20260828-003`: `Kto` is a list of assignees on
     * a task and one responsible person on a level).
     *
     * Parsing once against the anchor and writing that value everywhere was correct only while
     * every row a column reached meant the same thing by it. It stopped being correct the
     * moment one column applied to two kinds — a fan-out from a level would have written a bare
     * id into forty tasks that expect a list, and nothing about it is a type error.
     */
    const previous = new Map<CellKey, unknown>();
    const byValue = new Map<string, { value: unknown; rowIds: string[] }>();
    for (const line of reached) {
      const row = shown(line);
      const one = edit.parse(input, row);
      // A row that cannot take this input contributes nothing and is not an error — §C.1's
      // filter, applied to the value rather than to the row.
      if (!one.ok) continue;
      previous.set(
        cellKey({ rowId: line.id, colKey: ref.colKey }),
        edit.current(row),
      );
      const key = JSON.stringify(one.value ?? null);
      const bucket = byValue.get(key) ?? { value: one.value, rowIds: [] };
      bucket.rowIds.push(line.id);
      byValue.set(key, bucket);
    }
    if (byValue.size === 0) {
      editing.cancel();
      return;
    }

    void editing.apply({
      batches: [{ colKey: ref.colKey, groups: [...byValue.values()] }],
      previous,
    });
    if (andMove !== null)
      move(andMove === "down" ? 1 : 0, andMove === "right" ? 1 : 0, false);
  };

  /**
   * `Delete` / `Backspace` over the selection — `KLQ-20260828-001` §B, the last row of the
   * gesture table.
   *
   * It is the same act as typing nothing into every selected cell at once, so it is the same
   * machinery: one `EditBatch` per column, handed to `apply()` **together** as one call.
   * That is what makes twelve emptied cells across three columns come back with a single
   * `Cmd+Z`, and it is why this builds batches rather than calling `commitEdit` once per
   * column — three calls would be three history entries and three presses to undo one act.
   *
   * **What is skipped, and why silently.** Three filters, all of them already declared:
   *
   *   - a column with no `edit` is read-only for this door as for every other (§A.2);
   *   - a row whose kind has no such field is filtered by `applies`, never a reason to refuse
   *     the whole gesture (§C.1, `KLQ-20260825-006` §D.4) — a container caught in a drag
   *     across the tree contributes nothing and says nothing about it;
   *   - **a column that cannot hold empty is skipped too**, and the column already says which
   *     it is: `parse("")` is exactly the question *"what does an empty box mean here?"*. A
   *     date answers `null`, a list of people answers `[]`, and a status answers that a task
   *     always has one and refuses. No new field beside `parse`, and no list of required
   *     columns to keep in step with the columns — the same declaration that makes a blank
   *     cell in a pasted sheet leave `state` alone makes `Delete` leave it alone.
   *
   * A selection of nothing but skipped cells is therefore a no-op rather than an error: the
   * person pressed a key over cells that hold nothing they may empty, and saying so would be
   * noise on a gesture whose whole point is that it is cheap.
   */
  const clearCells = (ask: boolean): void => {
    if (selection === null || editing === null) return;

    const previous = new Map<CellKey, unknown>();
    const batches: EditBatch[] = [];
    const rows = new Set<string>();

    for (const column of columns) {
      const edit = column.edit;
      if (edit === undefined) continue;
      // Grouped by the value emptiness means for each row — one group in practice, but
      // `parse` is a function of the row and this keeps that true rather than assuming it.
      const byValue = new Map<string, { value: unknown; rowIds: string[] }>();
      for (const line of drawn) {
        if (!selection.isSelected({ rowId: line.id, colKey: column.key }))
          continue;
        const row = shown(line);
        if (!edit.applies(row)) continue;
        const empty = edit.parse("", row);
        if (!empty.ok) continue;

        previous.set(
          cellKey({ rowId: line.id, colKey: column.key }),
          edit.current(row),
        );
        const key = JSON.stringify(empty.value ?? null);
        const bucket = byValue.get(key) ?? { value: empty.value, rowIds: [] };
        bucket.rowIds.push(line.id);
        byValue.set(key, bucket);
        rows.add(line.id);
      }
      if (byValue.size > 0)
        batches.push({ colKey: column.key, groups: [...byValue.values()] });
    }

    if (batches.length === 0) return;
    // **Always asked, not only above the fan-out threshold** (owner, 2026-08-28). An edit
    // typed into a cell is a value the person chose; a `Delete` is a key that sits next to
    // the arrows and fires with nothing to look at. Clearing is undoable, but an undo that
    // has to be *noticed* first is not a safety net — and the row count in the question is
    // what tells somebody who pressed it by accident that anything happened at all.
    // Counted in rows and not in cells — a row is what the person selected and what they see.
    if (ask && rows.size >= 1) {
      setClearing({ batches, previous, rows: rows.size });
      return;
    }
    setClearing(null);
    void editing.apply({ batches, previous });
  };

  /**
   * A Sheets block, onto the cells — `KLQ-20260824-002` (parse) meeting
   * `KLQ-20260828-001` (write).
   *
   * Where the block lands: a selection of more than one cell **is** the target, and a source
   * smaller than it repeats over it — which is what a spreadsheet does when one cell is pasted
   * over ten, and what makes "set all of these to Friday" one gesture. A single selected cell
   * is an anchor instead, and the block extends down and right from it.
   *
   * What is refused, quietly and per cell: a column with no `edit` (read-only for typing and
   * for paste alike — one declaration, both doors), a row whose kind has no such field, and a
   * value the column's `parse` rejects. The rest of the block still lands: a sheet with one
   * bad date in it is not a reason to write none of it.
   *
   * The whole paste is **one** act for undo, however many columns and values it took.
   */
  const paste = async (): Promise<void> => {
    if (selection === null) return;

    const text = await readClipboard();
    if (text === null || text.trim() === "") return;
    const source = parseClipboard(text);
    if (source.length === 0) return;

    /*
     * The branch, and it is the whole of the decision (`KLQ-20260824-002` §Paste, amended
     * 2026-08-28): **nothing selected means import.** One keystroke, two destinations, chosen
     * only by what is selected — never by a mode, a button or a second chord to remember.
     *
     * A surface with no adapter says so. It used to return here in silence, which is the one
     * behaviour in this file a person cannot distinguish from a broken table.
     */
    if (selection.cells.size === 0) {
      if (importAdapter === undefined) {
        setNotice(t("import.unsupported"));
        return;
      }
      setImporting(source);
      return;
    }

    if (editing === null) return;
    const anchor = selection.focus;
    if (anchor === null) return;

    const selectedRows = rowIds.filter((rowId) =>
      columns.some((column) =>
        selection.isSelected({ rowId, colKey: column.key }),
      ),
    );
    const selectedCols = columns
      .map((column) => column.key)
      .filter((colKey) =>
        rowIds.some((rowId) => selection.isSelected({ rowId, colKey })),
      );

    const anchorRow = rowIds.indexOf(anchor.rowId);
    const anchorCol = columns.findIndex(
      (column) => column.key === anchor.colKey,
    );
    if (anchorRow === -1 || anchorCol === -1) return;

    // More than one cell selected → the selection is the target and the source repeats.
    // Otherwise the anchor is the top-left corner and the source's own size decides.
    const spread = selectedRows.length > 1 || selectedCols.length > 1;
    const targetRows = spread
      ? selectedRows
      : rowIds.slice(anchorRow, anchorRow + source.length);
    const targetCols = spread
      ? selectedCols
      : columns
          .slice(anchorCol, anchorCol + (source[0]?.length ?? 0))
          .map((column) => column.key);

    const previous = new Map<CellKey, unknown>();
    const batches: EditBatch[] = [];

    targetCols.forEach((colKey, columnIndex) => {
      const column = byColumnKey.get(colKey);
      const edit = column?.edit;
      if (edit === undefined) return;

      const byValue = new Map<string, { value: unknown; rowIds: string[] }>();
      targetRows.forEach((rowId, rowIndex) => {
        const line = drawn.find((one) => one.id === rowId);
        if (line === undefined || !edit.applies(shown(line))) return;
        const sourceRow = source[rowIndex % source.length];
        const input = sourceRow?.[columnIndex % Math.max(sourceRow.length, 1)];
        if (input === undefined) return;
        const parsed = edit.parse(input, shown(line));
        if (!parsed.ok) return;

        previous.set(cellKey({ rowId, colKey }), edit.current(shown(line)));
        const key = JSON.stringify(parsed.value ?? null);
        const bucket = byValue.get(key) ?? { value: parsed.value, rowIds: [] };
        bucket.rowIds.push(rowId);
        byValue.set(key, bucket);
      });

      if (byValue.size > 0)
        batches.push({ colKey, groups: [...byValue.values()] });
    });

    if (batches.length > 0) await editing.apply({ batches, previous });
  };

  // The two menu items declared far above hold these by reference, so every render rebinds
  // what they *do* without any of them changing identity.
  doCopy.current = copy;
  doPaste.current = paste;

  const onKeyDown = (event: KeyboardEvent<HTMLTableElement>): void => {
    if (selection === null) return;
    // Whatever the notice was about, the person has moved on. Tied to the next keystroke
    // rather than to a timer: a message that vanishes on its own is one somebody reads half of.
    if (notice !== null) setNotice(null);
    const additive = event.metaKey || event.ctrlKey;

    // Cmd/Ctrl+C is bound on the table rather than on the window. Ayon binds globally and
    // then spends a screen of guards deciding whether the person meant the grid or a text
    // selection somewhere else on the page (`ClipboardContext.tsx`, the `handleKeyDown`
    // block); a table that only claims the chord while it has focus needs none of them.
    if (additive && event.key.toLowerCase() === "c") {
      copy();
      return;
    }
    if (
      (editing !== null || importAdapter !== undefined) &&
      additive &&
      event.key.toLowerCase() === "v"
    ) {
      event.preventDefault();
      void paste();
      return;
    }

    // Cmd/Ctrl+A belongs to the table a person is reading — the filter rail beside it
    // deliberately does not claim the chord (`components/slicer`, and §B.2's note).
    if (additive && event.key.toLowerCase() === "a") {
      event.preventDefault();
      selection.selectAll();
      return;
    }
    // Undo is the batch, not the cell (§F.2): one fan-out over twelve rows comes back as one
    // act, restoring each row's own previous value.
    if (editing !== null && additive && event.key.toLowerCase() === "z") {
      event.preventDefault();
      void (event.shiftKey ? editing.redo() : editing.undo());
      return;
    }
    // Shift+F10 and the ContextMenu key are the menu's keyboard door (§I) — the one that
    // makes every action reachable without a right-click, which is also every action reachable
    // on a machine with no right button.
    if (
      menuItems !== undefined &&
      selection.focus !== null &&
      (event.key === "ContextMenu" || (event.shiftKey && event.key === "F10"))
    ) {
      event.preventDefault();
      // Anchored to the focused cell's own corner rather than to the pointer, which may be
      // anywhere on the screen or nowhere at all.
      const box = document
        .getElementById(domId(selection.focus))
        ?.getBoundingClientRect();
      openMenu(selection.focus, {
        x: box === undefined ? 0 : box.left,
        y: box === undefined ? 0 : box.bottom,
      });
      return;
    }
    // While the clear is waiting for an answer it owns the two keys that answer it, and
    // nothing else: `Escape` must take back the question rather than the selection it is
    // about, and `Enter` must not open an editor underneath an unanswered one.
    if (clearing !== null && (event.key === "Escape" || event.key === "Enter")) {
      event.preventDefault();
      const pending = clearing;
      setClearing(null);
      if (event.key === "Enter")
        void editing?.apply({
          batches: pending.batches,
          previous: pending.previous,
        });
      return;
    }
    // Emptying every cell of the selection that can be emptied, as one act (§B).
    if (event.key === "Delete" || event.key === "Backspace") {
      // Never while a cell is open: there `Delete` deletes a character, and the editor's own
      // handler stops the key before it arrives. This is the table's key only while the
      // table itself has the focus.
      //
      // Two statements rather than one chained condition: `a === null || a.b` is the shape
      // `prefer-optional-chain` rewrites to `a?.b`, and that rewrite would fold *"there is no
      // editing provider"* into *"an editor is open"* — two different reasons to do nothing,
      // and only one of them is about this keystroke.
      if (editing === null) return;
      if (editing.editing !== null || inField(event.target)) return;
      event.preventDefault();
      clearCells(true);
      return;
    }
    if (event.key === "Escape") {
      selection.clear();
      return;
    }
    if (event.key === "Enter" && selection.focus !== null) {
      const column = columns.find((one) => one.key === selection.focus?.colKey);
      if (column !== undefined) {
        event.preventDefault();
        setTyped(null);
        startEditing(selection.focus, column);
      }
      return;
    }
    // A printable character opens the cell and replaces its contents. Modifier chords are
    // already handled above, so what is left here is somebody typing into a table.
    if (
      selection.focus !== null &&
      !additive &&
      !event.altKey &&
      event.key.length === 1 &&
      event.key !== " "
    ) {
      const column = columns.find((one) => one.key === selection.focus?.colKey);
      // A picker takes a typed character too, now that it has somewhere to put one: the
      // character opens the list and seeds its search (§B.3). Before the list existed, a
      // native `<select>` had nothing to do with the key but swallow it.
      if (column?.edit !== undefined) {
        event.preventDefault();
        setTyped(event.key);
        startEditing(selection.focus, column);
      }
    }
    if (event.key === " " && selection.focus !== null) {
      event.preventDefault();
      // Space is the keyboard's tick, so it asks the tick's question and not the grid's. A
      // container is now reachable by the arrows (`focusable`) and still may not be ticked
      // (`canSelect`) — without this the key would do by keyboard exactly what the missing
      // checkbox refuses to do by mouse.
      const here = drawn.find((one) => one.id === selection.focus?.rowId);
      if (here !== undefined && canSelect(here)) {
        selection.pressRow(selection.focus.rowId, {
          shift: false,
          additive: false,
        });
      }
      return;
    }
    /**
     * **The twisty, from the keyboard** — the treegrid binding, and it stopped being optional
     * on 2026-09-10.
     *
     * While the tree opened fully there was always another way down: a branch a reader could
     * not open was a branch already open. Collapsing by default (`KLQ-20260825-008` §5.1)
     * removes that, and without this a keyboard user could reach a shut branch, see it is
     * shut, and have no key that opens it — the twisty is a button inside a cell of an
     * `aria-activedescendant` grid, so Tab does not reach it either. That is not a slow path;
     * it is no path.
     *
     * WAI-ARIA's own bindings, and they fall out of what the keys already mean here:
     * `ArrowRight` on a shut branch opens it, `ArrowLeft` on an open one shuts it, and in every
     * other case both keys stay the column move they were.
     */
    const here = drawn.find((one) => one.id === selection.focus?.rowId);
    if (here?.expandable === true) {
      if (event.key === "ArrowRight" && !here.open) {
        event.preventDefault();
        toggle(here.id);
        return;
      }
      if (event.key === "ArrowLeft" && here.open) {
        event.preventDefault();
        toggle(here.id);
        return;
      }
    }

    const step: Record<string, readonly [number, number]> = {
      ArrowUp: [-1, 0],
      ArrowDown: [1, 0],
      ArrowLeft: [0, -1],
      ArrowRight: [0, 1],
    };
    const delta = step[event.key];
    if (delta !== undefined) {
      event.preventDefault();
      move(delta[0], delta[1], event.shiftKey);
    }
  };

  /** The twisty's one job in either layout: open what is closed, close what is open. */
  const toggle = (id: string): void => {
    setCollapsed((current) => {
      // `current ?? byDefault` is where the derived default becomes the reader's own set. From
      // here on the tree is theirs and no data arriving can re-close what they opened.
      const next = new Set(current ?? byDefault);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /**
   * The narrow half (§C): one card per row, two lines, nothing else.
   *
   * The rows are the ones the grid draws — same `drawn`, same `mode`, same collapsed set — so
   * a branch opened on a phone is the branch that is open when the window is widened. What is
   * absent is absent on purpose and is listed in the module header: no select column, no cell
   * refs, no drag, no editor, no context menu. Tapping a leaf does whatever the title cell
   * already does, because both consumers draw their own link or button in it.
   */
  const list = (
    <ul className={cards.list} aria-label={label}>
      {drawn.map((line) => {
        const row = shown(line);
        // 12px per depth rather than the grid's 18px (§C): four levels deep still leaves
        // ~280px for text on a 390px phone.
        const indent = { paddingInlineStart: `${String(8 + line.depth * 12)}px` };
        /**
         * The twisty is its own button, a *sibling* of the title cell — never a box drawn
         * around it. The grid draws it that way too (see the first column below), and for the
         * same reason: a title cell is routinely a link or a button of the consumer's own
         * (the drawings register's name cell is), and a control nested inside a control is
         * invalid HTML, a second tab stop, and one tap that both opens the row and collapses
         * the branch it is in. 44 x 44, so a thumb finds it.
         */
        const twisty = line.expandable ? (
          <Twisty
            size="touch"
            open={line.open}
            label={t(line.open ? "treeTable.collapse" : "treeTable.expand", {
              name: rowLabel(row),
            })}
            onToggle={() => {
              toggle(line.id);
            }}
          />
        ) : (
          // Nothing under it yet. The space is still held, so the titles of a level line up
          // whether or not their rows happen to have children.
          <TwistySpacer size="touch" />
        );

        // A sentence about the list — the branch's *more is coming* edge. It keeps its
        // indentation, so it is still visibly inside its branch, and loses everything a card
        // has: the box, the twisty column, the meta line. See `TreeTableNode.notice` for why
        // this is not `group`.
        if (line.notice) {
          return (
            <li
              key={line.id}
              className={cards.noticeLine}
              // The branch's indent *plus* the gutter a card's twisty occupies (44 + 8), so the
              // sentence starts where the titles above it start. A notice left at the plain
              // indent reads as a row whose twisty failed to draw. Computed here and not in the
              // stylesheet because the indent itself is inline and would win over it.
              style={{
                paddingInlineStart: `${String(8 + line.depth * 12 + 44 + 8)}px`,
              }}
            >
              {(card.title ?? first).cell(row)}
            </li>
          );
        }

        // Structure: the floor, the section, the bucket. `group` alone decides it — a data
        // row that merely owns children (a task with a deliverable) is still a card with its
        // own meta line, not a title-only heading. Rule 3 of §C.
        if (line.group) {
          return (
            <li
              key={line.id}
              className={[cards.line, cards.headingLine].join(" ")}
            >
              <div className={cards.heading} style={indent}>
                {twisty}
                <span className={cards.headingText}>
                  {(card.title ?? first).cell(row)}
                </span>
              </div>
            </li>
          );
        }

        return (
          <li key={line.id} className={cards.line}>
            <div className={cards.row} style={indent}>
              {twisty}
              <div className={cards.card}>
                <span className={cards.title}>
                  {(card.title ?? first).cell(row)}
                </span>
                <span className={cards.meta}>
                  {card.meta.map((column) => {
                    // A cell that renders *nothing* is skipped, or the separator between two
                    // facts would sit next to a fact that is not there. The test is the
                    // rendered cell rather than `value`: `value` is optional, and a column
                    // whose value is "" may still render something the phone needs (the
                    // drawings register's "no version" chip is exactly that).
                    const content = column.cell(row);
                    if (content == null || content === false || content === "")
                      return null;
                    return (
                      <span key={column.key} className={cards.metaItem}>
                        {content}
                      </span>
                    );
                  })}
                </span>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );

  return (
    <>
    {/* A neutral block and nothing more — `width: 100%; min-width: 0`, no overflow, no
        position, no containment — so the grid below lays out and its sticky header behaves
        exactly as it did before this wrapper existed. It is here to be measured. */}
    <div ref={setFrame} className={cards.frame}>
    {narrow ? list : (
    <table
      ref={table}
      className={[styles.table, selection === null ? "" : styles.picking]
        .filter(Boolean)
        .join(" ")}
      aria-label={label}
      aria-multiselectable={selection === null ? undefined : true}
      aria-activedescendant={
        selection?.focus == null ? undefined : domId(selection.focus)
      }
      tabIndex={selection === null ? undefined : 0}
      onKeyDown={onKeyDown}
      onMouseUp={selection?.endDrag}
      onMouseLeave={selection?.endDrag}
    >
      {/* The question a large `Delete` asks, in the editor's own words and at the editor's
          own threshold (§D.2). It is the table's `<caption>` because this gesture has no
          open cell to carry it — the person pressed a key over a block, and the block is the
          subject. It exists only while the question is unanswered. */}
      {notice === null ? (
        clearing === null ? null : (
        <caption className={styles.clearConfirm}>
          <span>{t("treeTable.clearAsk", { count: clearing.rows })}</span>
          <button
            type="button"
            onClick={() => {
              const pending = clearing;
              setClearing(null);
              // Both answers hand the focus back to the grid, the way the menu does on
              // Escape (§I): the button is about to stop existing, and focus falling to the
              // document body would leave the arrow keys pointing at nothing.
              table.current?.focus();
              void editing?.apply({
                batches: pending.batches,
                previous: pending.previous,
              });
            }}
          >
            {t("treeTable.editApply")}
          </button>
          <button
            type="button"
            onClick={() => {
              setClearing(null);
              table.current?.focus();
            }}
          >
            {t("treeTable.editCancel")}
          </button>
        </caption>
        )
      ) : (
        <caption className={styles.clearConfirm}>
          <span>{notice}</span>
        </caption>
      )}
      <colgroup>
        {selection === null ? null : <col style={{ width: "24px" }} />}
        {columns.map((column) => (
          <col key={column.key} style={{ width: column.width }} />
        ))}
      </colgroup>
      <thead>
        <tr>
          {selection === null ? null : (
            <th
              scope="col"
              className={[styles.head, styles.selectHead].join(" ")}
            >
              {/* Tri-state: nothing / a dash / a double check, and never a browser checkbox —
                  `role="checkbox"` with `aria-checked="mixed"` says the middle state to a
                  screen reader without inheriting a control we cannot style. */}
              <button
                type="button"
                role="checkbox"
                aria-checked={
                  selection.allRowsSelected
                    ? true
                    : selection.someRowsSelected
                      ? "mixed"
                      : false
                }
                aria-label={t("treeTable.selectAll")}
                className={styles.tick}
                onClick={selection.toggleAllRows}
              >
                <span className={styles.tickBox} aria-hidden>
                  {selection.allRowsSelected ? (
                    <CheckCheck size={12} strokeWidth={2} />
                  ) : selection.someRowsSelected ? (
                    <Minus size={12} strokeWidth={2} />
                  ) : null}
                </span>
              </button>
            </th>
          )}
          {columns.map((column) => (
            <th key={column.key} scope="col" className={styles.head}>
              {column.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody ref={view.ref}>
        {/* The undrawn rows, as height and nothing else. `aria-hidden` because there is nothing
            here to read, and a `<td>` because a `<tr>` with no cell is not a row. */}
        {view.window.above > 0 ? (
          <tr aria-hidden style={{ height: `${String(view.window.above)}px` }}>
            <td colSpan={spannedColumns} />
          </tr>
        ) : null}
        {drawn.slice(view.window.first, view.window.last).map((line) => {
          const selectableRow = selection !== null && canSelect(line);
          const rowSelected = selectableRow && selection.isRowSelected(line.id);
          const r = rowIds.indexOf(line.id);
          const failure = editing?.failures.get(line.id);

          /**
           * What one cell shows: the editor while it is open on this cell, the column's own
           * rendering otherwise. The editor replaces rather than overlays, so the cell keeps
           * its width and the table does not reflow around a person typing.
           */
          const content = (column: TreeTableColumn<Row>): ReactNode => {
            const ref: CellRef = { rowId: line.id, colKey: column.key };
            if (
              editing === null ||
              column.edit === undefined ||
              !editing.isEditing(ref)
            ) {
              return column.cell(shown(line));
            }
            const edit = column.edit;
            return (
              <CellEditor
                kind={editKindOf(edit.kind, shown(line))}
                label={column.header}
                options={edit.options?.(shown(line)) ?? []}
                // A picker seeds from the value it holds and filters by what was typed; a
                // text box seeds *from* the character, which is what starting a cell by
                // typing into it means.
                initial={
                  editKindOf(edit.kind, shown(line)) === "select" ||
                  editKindOf(edit.kind, shown(line)) === "multiselect"
                    ? seedFor(edit, column, shown(line))
                    : (typed ?? seedFor(edit, column, shown(line)))
                }
                typed={typed ?? undefined}
                count={fanOut(ref, column).length}
                // Both numbers come off the same fan-out, so they can never describe two
                // different selections (`KLQ-20260828-003` §F.2).
                reach={edit.reach?.(fanOut(ref, column).map((line) => shown(line)))}
                confirmAbove={CONFIRM_ABOVE}
                onCommit={(input, andMove) => {
                  setTyped(null);
                  commitEdit(ref, column, input, andMove);
                }}
                onCancel={() => {
                  setTyped(null);
                  editing.cancel();
                }}
              />
            );
          };

          /** One data cell, with its selection marks. Shared by the first column and the rest. */
          const cellProps = (colKey: string) => {
            if (selection === null || r === -1) {
              return { className: styles.cell } as const;
            }
            const ref: CellRef = { rowId: line.id, colKey };
            const on = selection.isSelected(ref);
            const c = colKeys.indexOf(colKey);
            return {
              id: domId(ref),
              "data-selected": on ? "" : undefined,
              className: [
                styles.cell,
                on ? styles.selectedCell : "",
                on ? edges(selection, rowIds, colKeys, r, c) : "",
                sameCell(selection.focus, ref) ? styles.focusedCell : "",
              ]
                .filter(Boolean)
                .join(" "),
              onMouseDown: (event: MouseEvent) => {
                // Shift-clicking otherwise drags a text selection across the table, which is
                // never what the chord means on a grid of rows.
                if (event.shiftKey) event.preventDefault();
                selection.press(ref, {
                  shift: event.shiftKey,
                  additive: event.metaKey || event.ctrlKey,
                });
              },
              onMouseEnter: (event: MouseEvent) => {
                // `buttons` rather than a flag: a mouseup that happened outside the table is
                // never delivered to it, and a drag left switched on would repaint the
                // selection on the next idle hover — which reads as the table having a mind
                // of its own.
                if (event.buttons !== 1) {
                  selection.endDrag();
                  return;
                }
                selection.drag(ref);
              },
              onDoubleClick: () => {
                const column = byColumnKey.get(colKey);
                if (column !== undefined) startEditing(ref, column);
              },
              onContextMenu: (event: MouseEvent) => {
                if (menuItems === undefined) return;
                // The browser's own menu is suppressed only where this one takes over: a
                // right-click on the page around the table still offers Back and Reload, and
                // Reload is a thing people legitimately want on a register.
                event.preventDefault();
                openMenu(ref, { x: event.clientX, y: event.clientY });
              },
            };
          };

          return (
            <tr
              key={line.id}
              className={[
                styles.row,
                line.group ? styles.groupRow : "",
                rowSelected ? styles.selectedRow : "",
                failure === undefined ? "" : styles.failedRow,
              ]
                .filter(Boolean)
                .join(" ")}
              // A row the server refused keeps its previous value and says why, while the
              // rows that succeeded keep the new one (§E.2). The reason is the server's, in
              // the surface's words — never "something went wrong".
              title={failure}
              aria-level={line.depth + 1}
              aria-expanded={line.expandable ? line.open : undefined}
              aria-selected={selectableRow ? rowSelected : undefined}
            >
              {selection === null ? null : (
                <td
                  // **The id `aria-activedescendant` points at when the focus is on the tick.**
                  // It is not decoration: `colKeys[0]` is this column, so it is where keyboard
                  // navigation *starts*, and `pressRow` parks the focus here after every row
                  // click. Without the id the table published an `aria-activedescendant`
                  // naming an element that does not exist, and a screen reader announces
                  // nothing at all for it — the row moves, the caret does not. Found by
                  // `e2e/bulk-actions-keyboard.spec.ts` on 2026-09-09, which read the
                  // attribute the way assistive technology does instead of asking
                  // `document.activeElement`.
                  id={domId({ rowId: line.id, colKey: SELECT_COLUMN_KEY })}
                  className={[styles.cell, styles.selectCell].join(" ")}
                  // The tick is part of the row, so a right-click on it means the row — a
                  // menu that appeared over every cell except the one people aim at when
                  // they are already thinking about a selection would be a strange gap.
                  onContextMenu={(event) => {
                    if (menuItems === undefined || !selectableRow) return;
                    event.preventDefault();
                    openMenu(
                      { rowId: line.id, colKey: SELECT_COLUMN_KEY },
                      { x: event.clientX, y: event.clientY },
                    );
                  }}
                >
                  {/* A group row is pointed at with the twisty, not ticked (rule 3 and the
                      `selectable` prop): it holds work, it is not work. */}
                  {selectableRow ? (
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={rowSelected}
                      aria-label={t("treeTable.selectRow", {
                        name: rowLabel(shown(line)),
                      })}
                      className={styles.tick}
                      onMouseDown={(event) => {
                        if (event.shiftKey) event.preventDefault();
                        selection.pressRow(line.id, {
                          shift: event.shiftKey,
                          additive: event.metaKey || event.ctrlKey,
                        });
                      }}
                      onMouseEnter={(event) => {
                        if (event.buttons !== 1) {
                          selection.endDrag();
                          return;
                        }
                        selection.drag({
                          rowId: line.id,
                          colKey: SELECT_COLUMN_KEY,
                        });
                      }}
                    >
                      {/* Nothing. The mark is the cell itself, painted by `.tick` — a glyph
                          inside a filled 24px square is the checkbox this stopped being. */}
                    </button>
                  ) : null}
                </td>
              )}

              {/* The indent is the first cell's own padding rather than a spacer element, so
                  a long name wraps against the text edge instead of under the twisty. */}
              <td
                {...cellProps(first.key)}
                {...titleOf(first, shown(line))}
                style={{
                  paddingInlineStart: `${String(8 + line.depth * 16)}px`,
                }}
              >
                <span className={styles.nameCell}>
                  {/* **Opening a branch is not visiting a cell**, and the press that says so
                      is inside `components/Twisty` rather than here: the twisty is drawn in
                      the name cell — it has to be, or the indent could not push it — so the
                      cell's own `mousedown` would otherwise park focus on the name, and a
                      branch a reader merely unfolded would become the selection. Reported by
                      the owner 2026-09-10, fixed here first, and moved into the shared
                      control when the slicer became the third list with a twisty. */}
                  {line.expandable ? (
                    <Twisty
                      open={line.open}
                      label={t(
                        line.open ? "treeTable.collapse" : "treeTable.expand",
                        { name: rowLabel(shown(line)) },
                      )}
                      onToggle={() => {
                        toggle(line.id);
                      }}
                    />
                  ) : (
                    <TwistySpacer />
                  )}
                  <span className={styles.nameText}>{content(first)}</span>
                </span>
              </td>
              {rest.map((column) => (
                <td
                  key={column.key}
                  {...cellProps(column.key)}
                  {...titleOf(column, shown(line))}
                >
                  {content(column)}
                </td>
              ))}
            </tr>
          );
        })}
        {view.window.below > 0 ? (
          <tr aria-hidden style={{ height: `${String(view.window.below)}px` }}>
            <td colSpan={spannedColumns} />
          </tr>
        ) : null}
      </tbody>
    </table>
    )}
    </div>
    {/* Beside the table rather than inside `<tbody>`: a `<div>` is not a legal child of a
        table, and a portal would be a second thing to keep positioned. */}
    {drawer}
    </>
  );
}
